
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';
import { ApprovalRequest, ApprovalModule, AttachmentInfo, ApprovalStatus } from '../types/legalOps';
import { db, storage, serverTimestamp, auth, collection, doc, addDoc, updateDoc, deleteDoc, getDoc } from '../../firebaseClient';
import { notifyActionRequired } from './notificationService';
import { LEGAL_EMAILS, getRoleByEmail, isSuperUser, isReadOnlyUser } from '../config/roles'; 
import { createOrResetApprovalFlow } from './approvalFlowService';
import { logActivity } from './activityLog';
import { canViewData } from '../utils/dataAccess';
import { syncContractReviewDueEvent } from './calendarService';
import { createLegalActivity } from './legalActivityService'; 
import { completeTaskBySource } from './taskService'; 

// [Synced] Approval Engine v4.6.1

const COLLECTION = 'approval_requests';

function getCurrentAppUser() {
    const u = auth.currentUser;
    if (!u) return null;
    const email = u.email || "";
    return {
        id: u.uid,
        email: email,
        name: u.displayName || "",
        role: getRoleByEmail(email),
        displayName: u.displayName || "",
        isReadOnly: isReadOnlyUser(email)
    };
}

export async function createApprovalRequest(
  payload: Omit<ApprovalRequest, 'id' | 'submittedAt' | 'updatedAt' | 'status'> & { status?: ApprovalStatus }
): Promise<string> {
  const applicantDept = payload.applicantDept || '';
  const mainDept = payload.department || '';
  
  const computedVisibleDepartments = Array.from(new Set([
      applicantDept,
      mainDept,
      ...(payload.visibleDepartments || [])
  ].filter(Boolean)));

  const docData = {
    ...payload,
    applicantDept, 
    visibleDepartments: computedVisibleDepartments,
    status: payload.status || 'SUBMITTED', 
    submittedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  
  const colRef = collection(db, COLLECTION);
  const docRef = await addDoc(colRef, docData);
  await updateDoc(docRef, { id: docRef.id });

  let auditModule = payload.module as string;
  if (auditModule === 'sealRequest' || auditModule === 'SEAL_REQUEST') auditModule = 'seal';

  // 1. Audit Log (Backend)
  await logActivity({
    module: auditModule as any,
    action: "submit", 
    entityId: docRef.id,
    entityType: "ApprovalRequest",
    entityTitle: payload.title,
    actorId: payload.requesterUid,
    actorName: payload.requesterName,
    details: `Submitted approval request: ${payload.title} (${payload.module})`
  });

  // 2. Create "Pending Task" for Legal/Assignee (Frontend WorkLog)
  let logModule = "OTHER";
  let taskTitlePrefix = "審核申請";
  
  if (payload.module === 'contractDraft') {
      logModule = "contractDraft";
      taskTitlePrefix = "合約會審";
  } else if (payload.module === 'sealRequest' || payload.module === 'SEAL_REQUEST') {
      logModule = "sealRequest";
      taskTitlePrefix = "用印申請";
  } else if (payload.module === 'govLetter') {
      logModule = "govCorrespondence";
      taskTitlePrefix = "公文函覆";
  } else if (payload.module === 'GOV_PROJECT') {
      logModule = "GOV_PROJECT";
      taskTitlePrefix = "法遵專案簽核";
  }

  // Only create task if status is IN_REVIEW or SUBMITTED
  if (docData.status === 'IN_REVIEW' || docData.status === 'SUBMITTED') {
      await createLegalActivity({
          sourceType: logModule,
          sourceId: docRef.id,
          title: `${taskTitlePrefix}：${payload.title}`,
          actorUserId: payload.requesterUid,
          actorUserName: payload.requesterName,
          actionLabel: "送出申請",
          todoRequired: true, 
          assigneeName: "Legal Team", 
          assigneeId: "group_legal",
          description: `申請人：${payload.requesterName}\n部門：${payload.department}\n說明：${payload.description}`,
          priority: (payload.priority === 'urgent' || payload.priority === 'critical') ? 'High' : 'Medium'
      });
  }

  return docRef.id;
}

export async function updateApprovalRequest(
  id: string,
  patch: Partial<ApprovalRequest>
): Promise<void> {
  const currentUser = auth.currentUser;
  
  const docRef = doc(db, COLLECTION, id);
  await updateDoc(docRef, {
    ...patch,
    updatedAt: serverTimestamp(),
  });

  // Auto-Close Task if Status is Terminal
  if (patch.status) {
      const terminalStatuses = ['APPROVED', 'FULLY_APPROVED', 'REJECTED', 'PUBLISHED', 'COMPLETED', 'LEGAL_RETURNED', 'RETURNED', 'CANCELLED'];
      if (terminalStatuses.includes(patch.status)) {
          await completeTaskBySource(id);
      }
  }

  if (currentUser && isSuperUser(currentUser.email)) {
      await logActivity({
        module: 'seal', 
        action: 'update',
        entityId: id,
        entityType: 'ApprovalRequest',
        actorId: currentUser.uid,
        actorName: currentUser.displayName || 'SuperUser',
        details: `SuperUser update`
      });
  }
}

export async function deleteApprovalRequest(id: string, actorInfo?: { uid: string, name: string }): Promise<void> {
  const docRef = doc(db, COLLECTION, id);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists) throw new Error("Document not found");
  
  const data = docSnap.data();
  const currentUser = auth.currentUser;
  
  if (!currentUser) throw new Error("Unauthorized");
  if (!isSuperUser(currentUser.email)) {
      // Allow requester to delete DRAFT
      if (data?.status === 'DRAFT' && data?.requesterUid === currentUser.uid) {
          // OK
      } else {
          throw new Error("403 Forbidden");
      }
  }

  await deleteDoc(docRef);

  await completeTaskBySource(id);

  await logActivity({
    module: "seal",
    action: "delete",
    entityId: id,
    entityType: "SealRequest",
    actorId: currentUser.uid,
    actorName: currentUser.displayName || "Admin",
    details: `Deleted request: ${data?.title}`
  });
}

export async function listApprovalRequestsByModule(
  module: ApprovalModule,
  onSnapshot: (items: ApprovalRequest[]) => void,
  onError?: (error: Error) => void
): Promise<() => void> {
  const currentUser = getCurrentAppUser();
  const q = db.collection(COLLECTION).where('module', '==', module);

  return q.onSnapshot((snap: any) => {
      const all = snap.docs.map((d: any) => ({ id: d.id, ...d.data() } as ApprovalRequest));
      
      all.sort((a: ApprovalRequest, b: ApprovalRequest) => {
          const ta = a.submittedAt?.toDate ? a.submittedAt.toDate().getTime() : (a.createdAt?.toDate ? a.createdAt.toDate() : 0);
          const tb = b.submittedAt?.toDate ? b.submittedAt.toDate().getTime() : (b.createdAt?.toDate ? b.createdAt.toDate() : 0);
          return tb - ta;
      });

      let filtered = all;
      if (currentUser) {
          filtered = filtered.filter((item: ApprovalRequest) => canViewData(currentUser, item));
      }
      onSnapshot(filtered);
    }, (err: any) => {
      console.error("[listApprovalRequestsByModule] Query Error", err);
      if (onError) onError(err);
    });
}

export async function uploadApprovalFile(
  docId: string,
  kind: 'request' | 'review' | 'ai_temp',
  file: File,
  uploaderEmail: string
): Promise<AttachmentInfo> {
  const fileId = Math.random().toString(36).substring(2) + Date.now();
  const path = `approval_requests/${docId}/${kind}/${fileId}-${file.name}`;
  const ref = storage.ref(path);
  const snap = await ref.put(file);
  const url = await snap.ref.getDownloadURL();
  return {
    id: fileId, name: file.name, url, path, 
    size: file.size, contentType: file.type, uploadedAt: new Date().toISOString()
  };
}
